import { Button, Fieldset, Heading } from "@digdir/designsystemet-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "@tanstack/react-router";
import { FormProvider, type UseFormReturn, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { AvtaleDto, GetAvtalePrivatResponse } from "@/api/generated";
import { DescriptionList, DescriptionTerm, FormWrapper } from "@/components";
import { BestillingFormErrorSummary } from "@/features/bestillinger";
import {
  bestillerSelskapFormSchema,
  type bestillerSelskapFormValues,
  Tekstfelt,
  translationPrefixSelskapForm,
} from "@/features/bestillinger/forms";
import { NæringsbyggFields } from "@/features/shared/NaeringsbyggFields";
import { SelskapBoligTypeFields } from "@/features/shared/SelskapBoligTypeFields";
import { useSelskapsBoligType } from "@/features/shared/useSelskapsBoligType";
import { formatDateStringInterval, formatOrgNr } from "@/utils";
import { EndreAvtaleFeilet } from "./EndreAvtaleFeilet";
import { usePutSelskapsAvtale } from "./hooks/usePutSelskapsAvtale";

function initFormValues(avtale: AvtaleDto): bestillerSelskapFormValues {
  if (avtale.avtaleType !== "Selskap" || avtale.avtaleDetaljerSelskap == null) {
    throw new Error(`prop ${avtale} må være av type Selskap`);
  }

  return {
    boligType: avtale.avtaleDetaljerSelskap.boligType,
    kundenummer: avtale.kundenummer ?? "",
    målerId: avtale.målerId ?? "",
    antallBoenheter: `${avtale.avtaleDetaljerSelskap.antallBoenheter ?? ""}`,
    næringsarealProsent:
      avtale.avtaleDetaljerSelskap.næringsarealProsent?.toString() ?? "", // NB: Form does not accept more than 2 digits, but we trust our backend to not send more
    næringsdrivende:
      (avtale.avtaleDetaljerSelskap?.næringsarealProsent ?? 0) > 0
        ? "ja"
        : "nei",
  };
}

type EndreSelskapAvtaleFormProps = {
  avtale: GetAvtalePrivatResponse;
};

export const EndreSelskapAvtaleForm = ({
  avtale: { avtale },
}: EndreSelskapAvtaleFormProps) => {
  if (avtale.avtaleType !== "Selskap" || avtale.avtaleDetaljerSelskap == null) {
    throw new Error(`prop ${avtale} må være av type Selskap`);
  }
  const { t } = useTranslation();
  const router = useRouter();

  const initialValues = initFormValues(avtale);

  const form = useForm({
    resolver: zodResolver(bestillerSelskapFormSchema),
    values: initialValues,
    mode: "onSubmit",
  }) as UseFormReturn<bestillerSelskapFormValues>;

  const {
    isNæringsdrivende,
    isNæringsbyggMedBoliger,
    formDataIsNæringsdrivende,
    formDataIsNæringsbyggMedBoliger,
  } = useSelskapsBoligType(form);

  const { mutate, isError, isPending } = usePutSelskapsAvtale();

  const handleSubmit = (data: bestillerSelskapFormValues) => {
    mutate({
      body: {
        boligType: data.boligType,
        målerId: data.målerId,
        kundenummer: data.kundenummer,
        antallBoenheter: formDataIsNæringsbyggMedBoliger(data)
          ? 1
          : Number(data.antallBoenheter),

        næringsarealProsent: formDataIsNæringsdrivende(data)
          ? Number(data.næringsarealProsent)
          : 0,
      },
      path: {
        id: avtale.avtaleId,
      },
    });
  };

  const handleAvbryt = () => {
    router.navigate({ to: "/" });
  };

  return (
    <section>
      <FormProvider {...form}>
        <FormWrapper onSubmit={form.handleSubmit(handleSubmit)}>
          <section className="space-y-4">
            <div>
              <Heading data-size={"xs"} className="mb-3">
                {t("pages.endre.form.leverandør.tittel")}
              </Heading>
              <DescriptionList>
                <DescriptionTerm
                  tranlsationFeltKey="pages.endre.form.leverandør.navn"
                  value={avtale.leverandør.name}
                />
              </DescriptionList>
            </div>
            <div>
              <Heading data-size={"xs"} className="mb-3">
                {t("pages.endre.form.aktør.tittel")}
              </Heading>
              <DescriptionList>
                <DescriptionTerm
                  tranlsationFeltKey="pages.endre.form.aktør.navn"
                  value={avtale.avtaleEier.navn}
                />
                <DescriptionTerm
                  tranlsationFeltKey="pages.endre.form.aktør.organisasjonsnummer"
                  value={formatOrgNr(avtale.avtaleEier.organisasjonsnummer)}
                />
                <DescriptionTerm
                  tranlsationFeltKey="pages.endre.form.aktør.innsender"
                  value={avtale.bestiller.navn}
                />
              </DescriptionList>
            </div>
            <div>
              <Heading data-size={"xs"} className="mb-3">
                {t("pages.endre.form.avtaleperiode.tittel")}
              </Heading>
              <DescriptionList>
                <DescriptionTerm
                  tranlsationFeltKey="pages.endre.form.avtaleperiode.dato"
                  value={formatDateStringInterval(
                    avtale.activeFromInclusive,
                    avtale.activeToExclusive,
                  )}
                />
              </DescriptionList>
            </div>
          </section>
          <SelskapBoligTypeFields form={form} />
          <Fieldset>
            <Tekstfelt
              feltKey="kundenummer"
              maxLength={256}
              hjelpeTekst={true}
              labelTranslationPrefix={translationPrefixSelskapForm}
            />
          </Fieldset>
          <Fieldset>
            <Tekstfelt
              feltKey="målerId"
              maxLength={256}
              hjelpeTekst={true}
              labelTranslationPrefix={translationPrefixSelskapForm}
            />
          </Fieldset>
          <NæringsbyggFields
            form={form}
            isNæringsbyggMedBoliger={isNæringsbyggMedBoliger}
            isNæringsdrivende={isNæringsdrivende}
            readOnly={false}
          />
          {isError && <EndreAvtaleFeilet />}
          <BestillingFormErrorSummary errors={form.formState.errors} />
          <div className="flex gap-4">
            <Button onClick={handleAvbryt} variant="secondary">
              <span>{t("pages.endre.knapper.avbryt")}</span>
            </Button>
            <Button type="submit" loading={isPending}>
              <span>{t("pages.endre.knapper.lagre")}</span>
            </Button>
          </div>
        </FormWrapper>
      </FormProvider>
    </section>
  );
};
